/**
 * @fileoverview Eurofarma Diretoria Dashboard page component
 * Displays statistics, quick access buttons, and alerts for Eurofarma executive users
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, intl, HTTP_STATUS } from "dmsbasereact";

import { DashboardLayout } from "../../components/Dashboard";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";
import { formatDate } from "../../utils/formatters";

// MUI Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

/**
 * EurofarmaDiretoriaDashboard - Dashboard page for Eurofarma executive users
 * @returns {JSX.Element} The Eurofarma Diretoria dashboard with stats, quick access, and alerts
 */
export const EurofarmaDiretoriaDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [alerts, setAlerts] = useState(null);

    /**
     * Fetch all dashboard data from API endpoints
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsResponse, alertsResponse] = await Promise.all([
                fetchData("/dashboard/diretoria/stats", "GET", true),
                fetchData("/dashboard/diretoria/alerts", "GET", true),
            ]);

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                setStats(statsResponse.data?.data);
            }

            if (alertsResponse.status === HTTP_STATUS.OK_200) {
                setAlerts(alertsResponse.data?.data);
            }
        } catch (err) {
            console.error("Error fetching Eurofarma Diretoria dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    /**
     * Format pending institution approvals alerts
     */
    const formatPendingApprovalsAlerts = () => {
        if (!alerts?.pending_institutions) return [];
        return alerts.pending_institutions.map((item) => {
            const approvalProgress = item.total_groups > 0
                ? `${item.approved_groups}/${item.total_groups} ${intl.get("GroupsApproved").d("grupos aprovados")}`
                : "";
            const groupInfo = item.user_groups_pending?.length > 0
                ? `${intl.get("YourGroup").d("Seu grupo")}: ${item.user_groups_pending.join(", ")}`
                : "";
            const description = [approvalProgress, groupInfo].filter(Boolean).join(" | ") ||
                (item.created_on ? formatDate(item.created_on) : "");

            const isCategoryRequest = item.record_type === "category_request";

            return {
                id: isCategoryRequest ? `cat_${item.category_request_pk}` : item.institution_pk,
                title: item.name,
                description: description,
                institution_pk: item.institution_pk,
                record_type: item.record_type,
            };
        });
    };

    /**
     * Format homologation pipeline alerts
     */
    const formatHomologationPipelineAlerts = () => {
        if (!alerts?.homologation_pipeline) return [];
        return alerts.homologation_pipeline.map((item) => ({
            id: item.institution_pk,
            title: item.name,
            description: item.pipeline_stage || item.status_label,
            institution_pk: item.institution_pk,
        }));
    };

    /**
     * Handle click on pending approval alert
     */
    const handleApprovalClick = () => {
        navigate(`${APP_ROUTES.InstitutionApproval}?tab=diretoria`);
    };

    /**
     * Handle click on homologation pipeline alert
     */
    const handlePipelineClick = () => {
        navigate(APP_ROUTES.InstitutionApproval);
    };

    // Stats configuration - Cards de Resumo
    const statsConfig = [
        {
            title: intl.get("Dashboard.Diretoria.HomologatedClients").d("Qualified Clients"),
            value: stats?.homologated_clients || 0,
            icon: CheckCircleIcon,
            color: APP_COLORS.system.success,
            onClick: () => navigate(APP_ROUTES.ReportsHomologatedClients),
        },
        {
            title: intl.get("Dashboard.Diretoria.PendingApprovalsDiretoria").d("Pending Approvals"),
            value: stats?.pending_approvals || 0,
            icon: PendingActionsIcon,
            color: APP_COLORS.system.warning,
            onClick: () => navigate(`${APP_ROUTES.InstitutionApproval}?tab=diretoria`),
        },
        {
            title: intl.get("Dashboard.Diretoria.ClientsInHomologation").d("Clients in Qualification"),
            value: stats?.clients_in_homologation || 0,
            icon: HourglassEmptyIcon,
            color: APP_COLORS.system.info,
            onClick: () => navigate(`${APP_ROUTES.InstitutionApproval}?tab=gestao_lena`),
        },
    ];

    // Quick access buttons
    const quickAccessConfig = [
        {
            label: intl.get("Dashboard.Diretoria.HomologatedClientsList").d("Qualified Clients"),
            icon: FactCheckIcon,
            route: APP_ROUTES.ReportsHomologatedClients,
            color: APP_COLORS.primary.main,
        },
        {
            label: intl.get("Dashboard.Diretoria.SalesReport").d("Sales Report"),
            icon: TrendingUpIcon,
            route: APP_ROUTES.AnalyticsSales,
            color: APP_COLORS.system.info,
        },
        {
            label: intl.get("Dashboard.Diretoria.PatientTraceability").d("Rastreabilidade Venda ao Paciente"),
            icon: CompareArrowsIcon,
            route: APP_ROUTES.AnalyticsTraceability,
            color: APP_COLORS.system.success,
        },
    ];

    // Alerts configuration
    const alertsConfig = [
        {
            title: intl.get("Dashboard.Diretoria.PendingApprovals").d("Aprovações Pendentes do Seu Grupo"),
            items: formatPendingApprovalsAlerts(),
            severity: "warning",
            emptyMessage: intl.get("Dashboard.Diretoria.NoPendingApprovals").d("Nenhuma aprovação pendente para seu grupo"),
            onItemClick: handleApprovalClick,
        },
        {
            title: intl.get("Dashboard.Diretoria.HomologationPipeline").d("Clientes em Homologação"),
            items: formatHomologationPipelineAlerts(),
            severity: "info",
            emptyMessage: intl.get("Dashboard.Diretoria.NoHomologationPipeline").d("Nenhum cliente em processo de homologação"),
            onItemClick: handlePipelineClick,
        },
    ];

    return (
        <DashboardLayout
            welcomeName={stats?.user_name}
            loading={loading}
            stats={statsConfig}
            quickAccess={quickAccessConfig}
            alerts={alertsConfig}
        />
    );
};
